import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { colors } from '../../tokens/colors';
import { durations, reducedMotionCSS } from '../../utils/motion';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastContextType {
  addToast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast(): ToastContextType {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast deve ser usado dentro de ToastProvider');
  }

  return context;
}

interface ToastProviderProps {
  children: ReactNode;
}

const variantStyles: Record<ToastVariant, { bg: string; border: string; icon: string }> = {
  success: {
    bg: colors.success[50],
    border: colors.success[200],
    icon: '✓',
  },
  error: {
    bg: colors.error[50],
    border: colors.error[200],
    icon: '✗',
  },
  warning: {
    bg: colors.warning[50],
    border: colors.warning[200],
    icon: '⚠',
  },
  info: {
    bg: colors.info[50],
    border: colors.info[200],
    icon: 'ℹ',
  },
};

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, variant: ToastVariant = 'info'): void => {
      const id = crypto.randomUUID();

      setToasts((prev) => [...prev, { id, message, variant }]);

      setTimeout(() => {
        removeToast(id);
      }, 5000);
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}

      <div
        className="toast-container"
        aria-live="polite"
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          zIndex: 20000,
          maxWidth: '400px',
        }}
      >
        {toasts.map((toast) => {
          const style = variantStyles[toast.variant];

          return (
            <div
              key={toast.id}
              className="toast-item"
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1rem',
                background: style.bg,
                border: `1px solid ${style.border}`,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                animation: `toast-slide-in ${durations.slide} ease`,
              }}
            >
              <span style={{ fontSize: '1.125rem' }}>{style.icon}</span>
              <span
                style={{
                  flex: 1,
                  fontSize: '0.875rem',
                  color: colors.text.primary,
                }}
              >
                {toast.message}
              </span>
              <button
                onClick={() => removeToast(toast.id)}
                aria-label="Fechar notificacao"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: colors.neutral[500],
                  fontSize: '1rem',
                  padding: '0.25rem',
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes toast-slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        ${reducedMotionCSS('.toast-item', '.toast-container')}
      `}</style>
    </ToastContext.Provider>
  );
}
